import { Controller } from "@hotwired/stimulus";
import { createConsumer } from "@rails/actioncable";

/**
 * Post Lock Channel Controller
 * 
 * Unified controller for handling all post lock and takeover request updates
 * via Action Cable WebSockets.
 * 
 * Subscribes to: post_lock_#{post_id}
 * Receives: Unified state with post lock data and all pending requests
 */
export default class extends Controller {
  static values = {
    postId: String,
    currentUserId: String,
    disabled: { type: Boolean, default: false }
  }

  connect() {
    console.log("🔌🔌🔌 POST LOCK CHANNEL CONTROLLER CONNECTING 🔌🔌🔌");
    console.log("  - Timestamp:", new Date().toISOString());
    console.log("  - Element:", this.element);
    console.log("  - Post ID Value:", this.postIdValue);
    console.log("  - Current User ID Value:", this.currentUserIdValue);
    console.log("  - Disabled:", this.disabledValue);
    console.log("  - All data attributes:", this.element?.dataset);
    
    // Check if ActionCable is disabled (polling is used instead)
    if (this.disabledValue) {
      console.log("⏸️ ActionCable disabled - polling will be used instead");
      return;
    }
    
    if (!this.postIdValue) {
      console.error("❌ Post Lock Channel: Post ID not found");
      console.error("  - Element:", this.element);
      console.error("  - Data attributes:", this.element?.dataset);
      return;
    }

    if (!this.currentUserIdValue) {
      console.error("❌ Post Lock Channel: Current User ID not found");
      console.error("  - Element:", this.element);
      console.error("  - Data attributes:", this.element?.dataset);
      return;
    }

    console.log("🔌 Post Lock Channel Controller connecting...", {
      postId: this.postIdValue,
      currentUserId: this.currentUserIdValue,
      element: this.element
    });

    // Create Action Cable consumer
    try {
      console.log("🔧 Creating Action Cable consumer...");
      console.log("  - createConsumer available:", typeof createConsumer);
      console.log("  - @rails/actioncable imported:", typeof createConsumer !== 'undefined');
      
      // Check cable URL (createConsumer uses default /cable if not specified)
      const cableUrl = document.querySelector('meta[name="action-cable-url"]')?.getAttribute('content');
      console.log("  - Cable URL meta tag:", cableUrl || "not found (using default /cable)");
      
      // createConsumer() uses /cable by default, or can take a URL
      this.consumer = cableUrl ? createConsumer(cableUrl) : createConsumer();
      console.log("✅ Action Cable consumer created successfully");
      console.log("  - Consumer:", this.consumer);
      console.log("  - Consumer type:", typeof this.consumer);
      console.log("  - Consumer subscriptions:", this.consumer?.subscriptions);
      
      // Check WebSocket connection
      if (this.consumer?.connection) {
        console.log("  - Connection object:", this.consumer.connection);
        console.log("  - Connection state:", this.consumer.connection.state);
        
        // Monitor connection state changes
        const originalOpen = this.consumer.connection.open;
        const originalClose = this.consumer.connection.close;
        const originalError = this.consumer.connection.error;
        
        this.consumer.connection.open = function(...args) {
          console.log("🔌🔌🔌 WebSocket OPENED");
          return originalOpen.apply(this, args);
        };
        
        this.consumer.connection.close = function(...args) {
          console.log("🔌🔌🔌 WebSocket CLOSED");
          return originalClose.apply(this, args);
        };
        
        this.consumer.connection.error = function(...args) {
          console.error("🔌🔌🔌 WebSocket ERROR:", args);
          return originalError.apply(this, args);
        };
      } else {
        console.warn("⚠️ No connection object found on consumer");
      }
    } catch (error) {
      console.error("❌ Failed to create Action Cable consumer:", error);
      console.error("  - Error name:", error.name);
      console.error("  - Error message:", error.message);
      console.error("  - Error stack:", error.stack);
      return;
    }
    
    // Subscribe to the unified channel
    const subscriptionParams = {
      channel: "PostLockChannel",
      post_id: this.postIdValue
    };
    
    console.log("📡 Creating subscription with params:", subscriptionParams);
    
    try {
      this.subscription = this.consumer.subscriptions.create(
        subscriptionParams,
        {
          connected: () => {
            console.log("✅✅✅ Connected to Post Lock Channel for post", this.postIdValue);
            console.log("  - Subscription:", this.subscription);
            console.log("  - Channel:", subscriptionParams.channel);
            console.log("  - Post ID:", subscriptionParams.post_id);
          },
          
          disconnected: () => {
            console.log("❌❌❌ Disconnected from Post Lock Channel");
            console.log("  - Post ID:", this.postIdValue);
          },
          
          received: (data) => {
            console.log("📨📨📨📨📨 ACTION CABLE MESSAGE RECEIVED 📨📨📨📨📨");
            console.log("  - Timestamp:", new Date().toISOString());
            console.log("  - Data type:", typeof data);
            console.log("  - Data keys:", Object.keys(data || {}));
            console.log("  - Full data:", JSON.stringify(data, null, 2));
            console.log("  - Post ID:", data?.post_id);
            console.log("  - Locked:", data?.locked);
            console.log("  - Locked by ID:", data?.locked_by_id);
            console.log("  - Pending requests:", data?.pending_requests?.length || 0);
            console.log("📨📨📨📨📨 END ACTION CABLE MESSAGE 📨📨📨📨📨");
            this.handleStateUpdate(data);
          },
          
          rejected: () => {
            console.error("❌❌❌ Subscription rejected for Post Lock Channel");
            console.error("  - Post ID:", this.postIdValue);
            console.error("  - User ID:", this.currentUserIdValue);
          }
        }
      );
      
      console.log("✅ Subscription object created:", this.subscription);
      console.log("  - Subscription state:", this.subscription?.state);
      console.log("  - Subscription identifier:", this.subscription?.identifier);
      
      // Check subscription after a short delay
      setTimeout(() => {
        console.log("⏱️ Checking subscription status after 1 second...");
        console.log("  - Subscription:", this.subscription);
        console.log("  - Subscription state:", this.subscription?.state);
        if (this.subscription?.state === "connected") {
          console.log("✅✅✅ Subscription is CONNECTED!");
        } else {
          console.warn("⚠️ Subscription state is:", this.subscription?.state);
        }
      }, 1000);
    } catch (error) {
      console.error("❌ Failed to create subscription:", error);
      console.error("  - Error name:", error.name);
      console.error("  - Error message:", error.message);
      console.error("  - Error stack:", error.stack);
    }
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      console.log("Unsubscribed from Post Lock Channel");
    }
  }

  handleStateUpdate(data) {
    console.log("🔄 handleStateUpdate called");
    console.log("  - Data received:", data);
    console.log("  - Data type:", typeof data);
    
    if (!data) {
      console.error("❌ No data received in handleStateUpdate");
      return;
    }
    
    if (data.type !== "post_lock_state") {
      console.warn("⚠️ Unknown message type:", data.type);
      console.warn("  - Expected: post_lock_state");
      console.warn("  - Received:", data.type);
      return;
    }

    console.log("✅ Valid post_lock_state message received");
    
    const isOwner = data.locked_by_id && data.locked_by_id.toString() === this.currentUserIdValue;
    const isRequester = (data.pending_requests || []).some(req => 
      req.requester_id.toString() === this.currentUserIdValue
    );
    const hasPendingRequest = (data.pending_requests || []).some(req => 
      req.requester_id.toString() === this.currentUserIdValue && req.status === "pending"
    );

    console.log("📊 State analysis:", {
      locked: data.locked,
      locked_by_id: data.locked_by_id,
      current_user_id: this.currentUserIdValue,
      isOwner,
      isRequester,
      hasPendingRequest,
      pendingRequestsCount: (data.pending_requests || []).length,
      pendingRequests: data.pending_requests
    });

    // Update UI based on user role
    if (isOwner) {
      console.log("👑 User is owner, updating owner UI");
      this.updateOwnerUI(data);
    } else if (hasPendingRequest) {
      console.log("🙋 User has pending request, updating requester UI");
      this.updateRequesterUI(data);
    } else if (data.locked && !isOwner) {
      console.log("🔒 Post locked by another user");
      // Check if user was previously a requester whose request was approved/denied
      this.handleLockStateForNonOwner(data);
    } else {
      console.log("🔓 Post unlocked or user can edit");
      this.updateUnlockedUI(data);
    }
  }
  
  handleLockStateForNonOwner(data) {
    // If the current user is now the lock owner, they got approved
    if (data.locked_by_id && data.locked_by_id.toString() === this.currentUserIdValue) {
      console.log("✅ User is now the lock owner - request was approved!");
      this.handleRequestApproved(data);
    } else {
      // Otherwise, just show the locked state
      this.updateLockedByOtherUI(data);
    }
  }

  updateOwnerUI(data) {
    console.log("👑👑👑 Updating Owner UI");
    console.log("  - Pending requests count:", data.pending_requests?.length || 0);
    console.log("  - Pending requests:", data.pending_requests);
    
    // Update pending requests list
    const requestsContainer = document.getElementById("lock-takeover-requests");
    const modalsContainer = document.getElementById("lock-takeover-modals");
    
    console.log("  - Requests container found:", !!requestsContainer);
    console.log("  - Modals container found:", !!modalsContainer);
    
    if (requestsContainer) {
      // Clear existing requests
      const currentCount = requestsContainer.children.length;
      console.log("  - Clearing existing requests (count:", currentCount, ")");
      requestsContainer.innerHTML = "";
      
      // Add each pending request (only those the current user can act on as lock owner)
      const currentUserId = this.currentUserIdValue.toString();
      const lockedById = data.locked_by_id?.toString();
      const pendingForOwner = (data.pending_requests || []).filter(
        r => r.status === "pending" && r.owner_id?.toString() === lockedById && lockedById === currentUserId
      );
      const pendingCount = pendingForOwner.length;
      console.log("  - Adding", pendingCount, "pending requests");
      
      pendingForOwner.forEach(request => {
        console.log("  - Adding pending request:", request.id);
        this.appendRequestToList(request);
        this.appendModalForRequest(request);
      });
      
      // Update count
      const heading = document.getElementById("pending-requests-heading");
      const parentContainer = document.getElementById("pending-takeover-requests-container");
      
      if (pendingCount > 0) {
        console.log("  - Showing requests container with", pendingCount, "requests");
        if (heading) {
          heading.textContent = `Pending Takeover Requests (${pendingCount})`;
        }
        if (parentContainer) {
          parentContainer.style.display = "none";
        }
      } else {
        console.log("  - Hiding requests container (no pending requests)");
        if (parentContainer) {
          parentContainer.style.display = "none";
        }
      }
    }
    
    // Remove modals for non-pending requests
    if (modalsContainer) {
      const allModals = modalsContainer.querySelectorAll('[id^="lock-takeover-modal-"]');
      console.log("  - Found", allModals.length, "existing modals");
      
      allModals.forEach(modal => {
        const requestId = modal.id.replace("lock-takeover-modal-", "");
        const request = (data.pending_requests || []).find(r => r.id.toString() === requestId);
        if (!request || request.status !== "pending") {
          console.log("  - Removing modal for request:", requestId, "status:", request?.status || "not found");
          const backdrop = document.getElementById(`lock-takeover-modal-backdrop-${requestId}`);
          modal.remove();
          if (backdrop) backdrop.remove();
        } else {
          console.log("  - Keeping modal for pending request:", requestId);
        }
      });
      
      // Remove body class if no modals
      const remainingModals = modalsContainer.querySelectorAll('.lock-takeover-modal-content');
      console.log("  - Remaining modals:", remainingModals.length);
      if (remainingModals.length === 0) {
        document.body.classList.remove('modal-open');
        console.log("  - Removed modal-open class from body");
      }
    }
    
    console.log("✅ Owner UI update completed");
  }

  updateRequesterUI(data) {
    console.log("🙋🙋🙋 Updating Requester UI");
    console.log("  - Pending requests:", data.pending_requests);
    console.log("  - Current user ID:", this.currentUserIdValue);
    console.log("  - Locked by ID:", data.locked_by_id);
    
    // Check if user is now the lock owner (request was approved)
    const isNowOwner = data.locked_by_id && data.locked_by_id.toString() === this.currentUserIdValue;
    
    if (isNowOwner) {
      console.log("✅✅✅ User is now the lock owner - request was approved!");
      this.handleRequestApproved(data);
      return;
    }

    // Always update "edited by" name when we have lock state (e.g. after lock transferred to someone else)
    this.updateLockWarningEditedByName(data.locked_by_name);
    
    // Find the user's pending request
    const userRequest = (data.pending_requests || []).find(req => 
      req.requester_id.toString() === this.currentUserIdValue && req.status === "pending"
    );
    
    if (userRequest) {
      console.log("⏳ User has pending request:", userRequest);
      // Update request status display
      const requestStatus = document.getElementById("lock-request-status");
      if (requestStatus) {
        this.fetchAndUpdateRequestStatus(userRequest.id);
      }
      return;
    }
    
    // No pending request found - request may have been denied/cancelled
    // Reload the page to get the latest state from server
    console.log("⚠️ No pending request found for user - reloading page");
    window.location.reload();
  }

  updateLockedByOtherUI(data) {
    console.log("🔒 Post locked by another user");
    
    // Show lock warning if not already shown
    const lockWarning = document.querySelector('.lock-warning-container');
    if (!lockWarning && data.locked_by_name) {
      // This would typically be handled by server-side rendering
      // But we can update any dynamic elements here
    }
  }

  updateUnlockedUI(data) {
    console.log("🔓 Post unlocked or user can edit");
    
    // Hide lock warnings if any
    const lockWarning = document.querySelector('.lock-warning-container');
    if (lockWarning) {
      lockWarning.style.display = "none";
    }
  }

  handleRequestApproved(data) {
    console.log("✅ Request approved - redirecting requester");
    
    const requestStatus = document.getElementById("lock-request-status");
    if (requestStatus) {
      requestStatus.innerHTML = `
        <div style="text-align: center; padding: 20px; background-color: #d4edda; border: 1px solid #28a745; border-radius: 4px; margin: 20px 0;">
          <p style="margin-bottom: 10px; color: #155724;">
            <strong>✅ Request Approved!</strong>
          </p>
          <p style="color: #155724; font-size: 14px;">
            Your request has been approved. Redirecting to edit page...
          </p>
        </div>
      `;
    }
    
    // Redirect to edit page
    const editUrl = `/admin/posts/${this.postIdValue}/edit`;
    if (!window.lockRedirectScheduled) {
      window.lockRedirectScheduled = true;
      setTimeout(() => {
        window.location.href = editUrl;
      }, 1500);
    }
  }


  appendRequestToList(request) {
    const container = document.getElementById("lock-takeover-requests");
    if (!container) {
      return;
    }
    
    // Check if already exists
    const existing = document.getElementById(`lock-takeover-request-${request.id}`);
    if (existing) {
      return;
    }
    
    // Fetch request HTML
    fetch(`/admin/posts/${this.postIdValue}/takeover_request_item?request_id=${request.id}`, {
      headers: {
        "Accept": "text/html",
        "X-CSRF-Token": this.getCSRFToken()
      }
    })
    .then(response => response.text())
    .then(html => {
      if (html.trim()) {
        container.insertAdjacentHTML("beforeend", html);
      }
    })
    .catch(error => {
      console.error("Error fetching request item:", error);
    });
  }

  appendModalForRequest(request) {
    const container = document.getElementById("lock-takeover-modals");
    if (!container) {
      return;
    }
    
    // Check if already exists
    const existing = document.getElementById(`lock-takeover-modal-${request.id}`);
    if (existing) {
      return;
    }
    
    // Fetch modal HTML from server
    fetch(`/admin/posts/${this.postIdValue}/takeover_request_item?request_id=${request.id}&modal=true`, {
      headers: {
        "Accept": "text/html",
        "X-CSRF-Token": this.getCSRFToken()
      }
    })
    .then(response => response.text())
    .then(html => {
      if (html.trim()) {
        container.insertAdjacentHTML("beforeend", html);
        document.body.classList.add('modal-open');
      }
    })
    .catch(error => {
      console.error("Error fetching modal:", error);
    });
  }

  fetchAndUpdateRequestStatus(requestId) {
    console.log("📡 Fetching request status for ID:", requestId);
    fetch(`/admin/posts/${this.postIdValue}/takeover_request_item?request_id=${requestId}`, {
      headers: {
        "Accept": "text/html",
        "X-CSRF-Token": this.getCSRFToken()
      }
    })
    .then(response => {
      console.log("✅ Received response for request status");
      return response.text();
    })
    .then(html => {
      console.log("📄 Received HTML length:", html.length);
      const requestStatus = document.getElementById("lock-request-status");
      if (requestStatus && html.trim()) {
        console.log("✅ Updating request status element");
        // Try to extract just the status part if it's a full request item
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const statusDiv = doc.querySelector('[id^="lock-request-status"]') || doc.body;
        requestStatus.innerHTML = statusDiv.innerHTML;
        console.log("✅ Request status updated successfully");
      } else {
        console.warn("⚠️ Request status element not found or empty HTML");
      }
    })
    .catch(error => {
      console.error("❌ Error fetching request status:", error);
    });
  }

  updateLockWarningEditedByName(lockedByName) {
    const nameEl = document.getElementById("lock-warning-edited-by-name");
    if (nameEl && lockedByName) {
      nameEl.textContent = lockedByName;
    }
  }

  getCSRFToken() {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.getAttribute("content") : "";
  }
}

